import * as React from 'react';
import { ActionLink, InformationTooltip, UIText1 } from '@cian/ui-kit';
import { IconActionChevronRightSmall16 } from '@cian/ui-kit-design-tokens/icons';
import * as style from './MultipleBuildersInfo.css';
import { BuilderLink } from '../BuilderLink';
import { BuildersTooltip } from '../BuildersTooltip';
import { INewbuildingBuilder } from '../../types/newbuilding/builder';

export interface IMultipleBuildersInfoProps {
  builders: INewbuildingBuilder[];
  onBuilderClick(): void;
}

export const MultipleBuildersInfo: React.FC<IMultipleBuildersInfoProps> = props => {
  const { builders, onBuilderClick } = props;

  const [isOpen, setIsOpen] = React.useState(false);
  const [isTruncated, setIsTruncated] = React.useState(false);
  const textRef = React.useRef<HTMLDivElement>(null);

  const moreButtonText = React.useMemo(() => `Ещё ${builders.length - 1}`, [builders.length]);

  React.useEffect(() => {
    const checkTruncation = () => {
      const element = textRef.current;
      if (element) {
        setIsTruncated(element.scrollWidth > element.clientWidth);
      }
    };

    checkTruncation();
    window.addEventListener('resize', checkTruncation);

    return () => {
      window.removeEventListener('resize', checkTruncation);
    };
  }, []);

  const onClick = React.useCallback(() => {
    setIsOpen(true);
  }, []);

  const onClose = React.useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <div className={style['container']}>
      <UIText1 color="text-primary-default">Застройщики</UIText1>
      <div className={style['names']}>
        <span className={style['names-text']} ref={textRef}>
          {builders.map((builder, index) => (
            <BuilderLink
              key={builder.id}
              text={builder.name + (index < builders.length - 1 ? ', ' : '')}
              linkUrl={builder.profileUrl}
              onClick={onBuilderClick}
              fontSize="M"
              inline
            />
          ))}
        </span>
        {isTruncated && (
          <div className={style['button']} data-testid="multiple-builders-info-button">
            <InformationTooltip
              open={isOpen}
              onClose={onClose}
              content={<BuildersTooltip builders={builders} onBuilderClick={onBuilderClick} />}
              placement="top-end"
            >
              <ActionLink
                color="current_color"
                onClick={onClick}
                fontSize="S"
                afterIcon={<IconActionChevronRightSmall16 color="current_color" />}
              >
                {moreButtonText}
              </ActionLink>
            </InformationTooltip>
          </div>
        )}
      </div>
    </div>
  );
};
